import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import {
	frontEndValidator as validator,
	passwordlessFrontEndValidator as passwordlessValidator
} from "lib/build/forms/common/signUp";

import { Button } from "src/common/components/Button";
import { Checkbox } from "src/common/components/Checkbox";
import { Field } from "src/common/components/Field";
import { Form, FormCustomContainer } from "src/common/components/Form";
import { GridColumn, GridRow } from "src/common/components/Grid";
import { Modal } from "src/common/components/Modal";
import { AuthPage } from "src/common/components/Page";
import { FullPagePreloader } from "src/common/components/Preloader";
import { TextCenter } from "src/common/components/TextAlign";
import { useQueryParam } from "src/common/hooks/useQueryParam";
import { api } from "src/common/utils/api";
import { AuthType } from "src/features/auth";
import {
	FieldData,
	FieldsetData,
	FieldsetRenderer,
	FormContextProvider,
	useFormState
} from "src/features/forms";
import { languageMessages, occupationMessages } from "src/messages/generic";
import { getToc } from "src/views/sign-up/getToc";
import { errors, labels, messages } from "src/views/sign-up/messages";
import { useInitialValue } from "src/views/sign-up/useInitialValue";
import { useSignUp } from "src/views/sign-up/useSignUp";

const errorMessages = {
	firstName: { presence: errors.firstNameRequired },
	lastName: { presence: errors.lastNameRequired },
	phoneNumber: {
		presence: errors.phoneNumberRequired,
		format: errors.phoneNumberFormat
	},
	occupation: { presence: errors.occupationRequired },
	stampNumber: {
		presence: errors.stampNumberRequired,
		format: errors.stampNumberFormat
	},
	language: { presence: errors.languageRequired },
	password: {
		presence: errors.passwordRequired,
		minLength: errors.passwordMinLength
	},
	passwordAgain: {
		presence: errors.passwordRequired,
		const: errors.passwordConst
	}
};

const fields: {
	[key: string]: FieldData;
} = {
	title: {
		type: "field",
		path: ["title"],
		label: labels.title,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	firstName: {
		type: "field",
		path: ["firstName"],
		label: labels.firstName,
		errorMessages: errorMessages.firstName,
		input: { type: "input", props: { type: "text" } }
	},
	lastName: {
		type: "field",
		path: ["lastName"],
		label: labels.lastName,
		errorMessages: errorMessages.lastName,
		input: { type: "input", props: { type: "text" } }
	},
	email: {
		type: "field",
		path: ["email"],
		label: labels.email,
		errorMessages: {},
		input: { type: "input", props: { type: "text", disabled: true } }
	},
	organization: {
		type: "field",
		path: ["organization"],
		label: labels.organization,
		errorMessages: {},
		input: { type: "input", props: { type: "text", disabled: true } }
	},
	module: {
		type: "field",
		path: ["module"],
		label: labels.module,
		errorMessages: {},
		input: { type: "input", props: { type: "text", disabled: true } }
	},
	phoneNumber: {
		type: "field",
		path: ["phoneNumber"],
		label: labels.phoneNumber,
		errorMessages: errorMessages.phoneNumber,
		input: { type: "input", props: { type: "phone-number" } }
	},
	occupation: {
		type: "field",
		path: ["occupation"],
		label: labels.occupation,
		errorMessages: errorMessages.occupation,
		input: { type: "select", props: { labels: occupationMessages } }
	},
	stampNumber: {
		type: "field",
		path: ["stampNumber"],
		label: labels.stampNumber,
		errorMessages: errorMessages.stampNumber,
		input: { type: "input", props: { type: "text" } }
	},
	language: {
		type: "field",
		path: ["defaultLanguage"],
		label: labels.language,
		errorMessages: errorMessages.language,
		input: { type: "select", props: { labels: languageMessages } }
	},
	password: {
		type: "field",
		path: ["password"],
		label: labels.password,
		errorMessages: errorMessages.password,
		input: {
			type: "input",
			props: { type: "password", autoComplete: "new-password" }
		}
	},
	passwordAgain: {
		type: "field",
		path: ["passwordAgain"],
		label: labels.passwordAgain,
		errorMessages: errorMessages.passwordAgain,
		input: {
			type: "input",
			props: { type: "password", autoComplete: "new-password" }
		}
	}
};

const passwordlessFieldset: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 2, field: fields.title },
				{ width: 5, field: fields.lastName },
				{ width: 5, field: fields.firstName }
			]
		},
		{ columns: [{ width: 12, field: fields.email }] },
		{
			columns: [
				{ width: 6, field: fields.organization },
				{ width: 6, field: fields.module }
			]
		},
		{ columns: [{ width: 12, field: fields.phoneNumber }] },
		{ columns: [{ width: 12, field: fields.occupation }] },
		{ columns: [{ width: 12, field: fields.stampNumber }] },
		{ columns: [{ width: 12, field: fields.language }] }
	]
};

const fieldset: FieldsetData = {
	...passwordlessFieldset,
	rows: [
		...passwordlessFieldset.rows,
		{
			columns: [
				{ width: 6, field: fields.password },
				{ width: 6, field: fields.passwordAgain }
			]
		}
	]
};

export const SignUp: React.FunctionComponent = () => {
	const token = useQueryParam("token") || "";
	const authType = useQueryParam("authType");

	if (authType === undefined) {
		throw new Error();
	}

	const [isLoading, setLoading] = React.useState(true);

	React.useEffect(() => {
		(async () => {
			try {
				await api.get(`/invites/${token}`);
				setLoading(false);
			} catch {
				window.location.href = "/sign-in";
			}
		})();
	}, [token]);

	return (
		<AuthPage>
			{isLoading ? (
				<FullPagePreloader />
			) : (
				<SignUpForm token={token} authType={authType} />
			)}
		</AuthPage>
	);
};

const SignUpForm: React.FunctionComponent<{
	token: string;
	authType: string;
}> = ({ token, authType }) => {
	const formState = useFormState({
		validator:
			authType === AuthType.Passwordless
				? passwordlessValidator
				: validator,
		initialValue: useInitialValue()
	});

	const ActualToc = getToc(useQueryParam("module"));

	const [{ showTOC, hasAcceptedTOC }, setState] = React.useState({
		showTOC: false,
		hasAcceptedTOC: false
	});

	const onOpenTOC = React.useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setState(state => ({
			...state,
			showTOC: true,
			hasAcceptedTOC: false
		}));
	}, []);

	const onCloseTOC = React.useCallback(() => {
		setState(state => ({ ...state, showTOC: false }));
	}, []);

	const onAcceptTOC = React.useCallback(() => {
		setState(state => ({
			...state,
			showTOC: false,
			hasAcceptedTOC: true
		}));
	}, []);

	const onChangeAcceptTOC = React.useCallback((isChecked: boolean) => {
		setState(state => ({
			...state,
			showTOC: isChecked,
			hasAcceptedTOC: false
		}));
	}, []);

	const { isSubmitted, isLoading, onSubmit } = useSignUp(
		token,
		formState.value,
		formState.errors.length === 0
	);

	return (
		<>
			{showTOC && (
				<Modal
					isFullSize={true}
					title={<FormattedMessage {...messages.toc} />}
					onClose={onCloseTOC}
					footer={
						<TextCenter>
							<Button type="button" onClick={onAcceptTOC}>
								<FormattedMessage {...messages.accept} />
							</Button>
						</TextCenter>
					}
				>
					<ActualToc />
				</Modal>
			)}
			<FormContextProvider {...formState}>
				<Form isSubmitted={isSubmitted} onSubmit={onSubmit}>
					<h1>
						<FormattedMessage {...messages.title} />
					</h1>
					<h2>
						<FormattedMessage {...messages.help} />
					</h2>
					<FieldsetRenderer
						{...(authType === AuthType.Passwordless
							? passwordlessFieldset
							: fieldset)}
					/>
					<GridRow>
						<GridColumn columnSize={12}>
							<Field>
								<Checkbox
									checked={hasAcceptedTOC}
									onChangeChecked={onChangeAcceptTOC}
								>
									<FormattedMessage
										{...labels.acceptTOCLeft}
									/>{" "}
									<span
										role="button"
										className="link"
										onMouseDown={onOpenTOC}
									>
										<FormattedMessage
											{...labels.acceptTOCRight}
										/>
									</span>
									.
								</Checkbox>
							</Field>
						</GridColumn>
					</GridRow>
					<GridRow>
						<GridColumn columnSize={12}>
							<FormCustomContainer>
								<TextCenter>
									<Button
										type="submit"
										disabled={!hasAcceptedTOC}
										isLoading={isLoading}
									>
										<FormattedMessage
											{...messages.submit}
										/>
									</Button>
								</TextCenter>
							</FormCustomContainer>
						</GridColumn>
					</GridRow>
					<GridRow>
						<GridColumn columnSize={12}>
							<FormCustomContainer>
								<TextCenter>
									<Link to="/sign-in">
										<FormattedMessage
											{...messages.signInLink}
										/>
									</Link>
								</TextCenter>
							</FormCustomContainer>
						</GridColumn>
					</GridRow>
				</Form>
			</FormContextProvider>
		</>
	);
};
